import { getAspectRatioForEndImages } from '@/utils/aspectRatioUtils';
import { getLinkAriaLabel } from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useRegionProvider } from '@/providers/RegionProvider';

import type { ImageWithFocalPointProps } from './BannerHero';

import * as S from './EndImage.styles';

export interface EndImageProps {
  endImage?: ImageWithFocalPointProps;
  endImageLinkType?: string | null;
  endImageUrl?: string | null;
  endImageUrlText?: string | null;
}

export const EndImage: React.FC<{ endImage: EndImageProps }> = ({
  endImage,
}) => {
  const { deviceType } = useDeviceDetect();
  const { locale } = useRegionProvider();

  // Create contextual aria-label: prefer dictionary lookup, then use CTA text
  const getContextualAriaLabel = () => {
    if (!endImage?.endImageUrl) return undefined;
    const dictionaryLabel = getLinkAriaLabel(endImage.endImageUrl, locale.code);
    if (dictionaryLabel) return dictionaryLabel;
    // Fallback: combine CTA text with context
    return endImage.endImageUrlText
      ? `${endImage.endImageUrlText} - View collection`
      : 'View collection';
  };

  const ariaLabel = getContextualAriaLabel();

  return (
    <S.EndImageContainer
      $hasUrl={!!endImage?.endImageUrl}
      {...(endImage?.endImageUrl && {
        'aria-label': ariaLabel,
      })}
      as={endImage?.endImageUrl ? LinkWithLocale : 'div'}
      {...(!!endImage?.endImageUrl && { href: endImage?.endImageUrl })}
      data-test-id='EndImageContainer'
    >
      <AspectRatioImage
        alt={`${endImage.endImageUrlText} ${endImage.endImageUrl}`}
        aspectRatio={getAspectRatioForEndImages(deviceType)}
        className='end-image image'
        data-test-id='EndImageAspectRatioImage'
        focalPoint={{
          x: endImage.endImage?.focalPoint?.focalPoint?.x ?? 0,
          y: endImage.endImage?.focalPoint?.focalPoint?.y ?? 0,
        }}
        loaderProps={{ height: 175 }}
        loading='lazy'
        originalImageDimensions={{
          x: endImage.endImage?.image?.width || 0,
          y: endImage.endImage?.image?.height || 0,
        }}
        showFallbackPlaceholder
        sizes='(max-width: 600px) 50vw, 20vw'
        src={endImage.endImage?.image?.url || ''}
        useLoader
        width='100%'
      />
      {endImage.endImageLinkType && (
        <S.EndImageButton
          {...(endImage.endImageUrl && {
            'aria-label': ariaLabel,
          })}
          colorVariant={
            endImage.endImageLinkType === 'button' ? 'primary' : 'white'
          }
          data-test-id='EndImageButton'
          label={endImage.endImageUrlText || ''}
          size='s'
          variant={endImage.endImageLinkType === 'button' ? 'filled' : 'link'}
        />
      )}
    </S.EndImageContainer>
  );
};
